import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/client';

const ROLE_LABELS = {
  private_person: 'Customer', vehicle_owner: 'Vehicle Owner', driver: 'Driver', bodaboda: 'Boda-Boda',
  hotel_host: 'Hotel Host', event_host: 'Event Host', admin: 'Admin',
};
const STATUSES = ['open', 'in_progress', 'resolved', 'closed'];

function fmt(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Attachments are stored as links or { url, name } objects.
function Attachments({ items }) {
  const list = Array.isArray(items) ? items : [];
  if (!list.length) return null;
  return (
    <div style={s.attach}>
      {list.map((a, i) => {
        const url = typeof a === 'string' ? a : a?.url || a?.signed_url || a?.path;
        const name = typeof a === 'string' ? a.split('/').pop() : a?.name || a?.filename || `Attachment ${i + 1}`;
        return url ? <a key={i} href={url} target="_blank" rel="noreferrer" style={s.attachLink}>📎 {name}</a> : <span key={i} style={s.attachLink}>📎 {name}</span>;
      })}
    </div>
  );
}

function Bubble({ m }) {
  const staff = m.sender_role === 'admin' || m.sender_role === 'support_agent';
  return (
    <div style={{ ...s.bubble, ...(m.is_internal_note ? s.noteBubble : staff ? s.adminBubble : s.userBubble) }}>
      <div style={s.bubbleMeta}>
        <span style={s.bubbleRole}>{m.is_internal_note ? '🔒 Internal note' : staff ? '🛡 Staff' : m.sender_role === 'system' ? '⚙ System' : '👤 Customer'}</span>
        <span>{fmt(m.created_at)}</span>
      </div>
      <div style={s.bubbleText}>{m.message}</div>
      <Attachments items={m.attachments} />
    </div>
  );
}

// Case Management › support case (Kayd, 26.10.2026): a ticket transferred to
// Case Management is opened and processed here — case and ticket number,
// creator, reason, the messages as they were at the transfer (incl.
// attachments), everything written since, and reply / internal note /
// status. Replies go to the customer in the same ticket conversation.
export default function SupportCase() {
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState(null);
  const [supportCase, setCase] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/support/admin/tickets/${ticketId}`);
      setTicket(res.data.ticket);
      setCase(res.data.case || null);
      setMessages(res.data.messages || []);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load this case.');
    } finally {
      setLoading(false);
    }
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const send = async (internal) => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/api/support/admin/tickets/${ticketId}/reply`, { message: text.trim(), internal });
      setText('');
      toast.success(internal ? 'Internal note saved' : 'Reply sent to the customer');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not send');
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/api/support/admin/tickets/${ticketId}/status`, { status });
      toast.success(`Status: ${status.replace('_', ' ')}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not change the status');
    }
  };

  if (loading) return <div style={s.page}><p>Loading…</p></div>;
  if (error || !ticket) {
    return (
      <div style={s.page}>
        <Link to="/case-control" style={s.back}>← Case Management</Link>
        <p style={{ color: '#b91c1c' }}>{error || 'Case not found.'}</p>
      </div>
    );
  }

  const snapshot = Array.isArray(supportCase?.messages_snapshot) ? supportCase.messages_snapshot : [];
  const snapIds = new Set(snapshot.map((m) => m.id));
  const since = messages.filter((m) => !snapIds.has(m.id));
  const creator = ticket.users || {};

  return (
    <div style={s.page}>
      <Link to="/case-control" style={s.back}>← Case Management</Link>
      <div style={s.head}>
        <div style={{ minWidth: 0 }}>
          <div style={s.numbers}>{supportCase?.case_number || 'Case'} · {ticket.ticket_number}</div>
          <h1 style={s.h1}>{ticket.subject}</h1>
        </div>
        <label style={s.statusBox}>
          <span style={s.label}>Status</span>
          <select style={s.select} value={ticket.status || 'open'} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((v) => <option key={v} value={v}>{v.replace('_', ' ')}</option>)}
          </select>
        </label>
      </div>

      {!supportCase && <div style={s.warn}>This ticket has not been transferred to Case Management.</div>}

      <div style={s.facts}>
        <Fact label="Created by" value={<>
          {supportCase?.creator_name || creator.full_name || creator.email || '—'}
          <div style={s.muted}>
            {ROLE_LABELS[supportCase?.creator_role || creator.user_role] || supportCase?.creator_role || creator.user_role || ''}
            {(supportCase?.creator_customer_number || creator.customer_number) ? ` · #${supportCase?.creator_customer_number || creator.customer_number}` : ''}
          </div>
          <div style={s.muted}>{supportCase?.creator_email || creator.email}</div>
        </>} />
        <Fact label="Ticket opened" value={fmt(ticket.created_at)} />
        <Fact label="Transferred" value={<>{fmt(supportCase?.created_at || ticket.transferred_to_case_at)}{supportCase?.transferred_by_name ? <div style={s.muted}>by {supportCase.transferred_by_name}</div> : null}</>} />
        <Fact label="Category · priority" value={`${ticket.category || '—'} · ${ticket.priority || 'normal'}`} />
        <Fact label="Country" value={ticket.country_code || supportCase?.country_code || '—'} />
        {supportCase?.reason && <Fact label="Reason for transfer" value={supportCase.reason} wide />}
      </div>

      <section style={s.section}>
        <h2 style={s.h2}>Conversation at transfer ({snapshot.length})</h2>
        {snapshot.length === 0 ? <p style={s.muted}>No messages at the time of transfer.</p> : snapshot.map((m) => <Bubble key={m.id} m={m} />)}
      </section>

      <section style={s.section}>
        <h2 style={s.h2}>Since the transfer ({since.length})</h2>
        {since.length === 0 ? <p style={s.muted}>Nothing new yet.</p> : since.map((m) => <Bubble key={m.id} m={m} />)}
        <textarea style={s.textarea} rows={4} placeholder="Write a reply to the customer, or an internal note for staff…" value={text} onChange={(e) => setText(e.target.value)} />
        <div style={s.actions}>
          <button type="button" style={s.secondary} disabled={!text.trim() || sending} onClick={() => send(true)}>Save as internal note</button>
          <button type="button" style={s.primary} disabled={!text.trim() || sending} onClick={() => send(false)}>{sending ? 'Sending…' : 'Send reply to customer'}</button>
        </div>
      </section>
    </div>
  );
}

function Fact({ label, value, wide }) {
  return (
    <div style={{ ...s.fact, ...(wide ? { gridColumn: '1 / -1' } : {}) }}>
      <div style={s.label}>{label}</div>
      <div style={s.value}>{value}</div>
    </div>
  );
}

const s = {
  page: { padding: 24, maxWidth: 900, boxSizing: 'border-box', width: '100%' },
  back: { color: '#0B6A4A', fontWeight: 700, textDecoration: 'none', fontSize: 13 },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', margin: '10px 0 14px' },
  numbers: { fontSize: 12, fontWeight: 700, color: '#64748b', letterSpacing: '0.03em' },
  h1: { fontSize: 22, fontWeight: 800, margin: '4px 0 0', wordBreak: 'break-word' },
  h2: { fontSize: 15, fontWeight: 700, margin: '0 0 10px' },
  statusBox: { display: 'flex', flexDirection: 'column', gap: 4 },
  select: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, textTransform: 'capitalize' },
  warn: { background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 },
  facts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 16 },
  fact: { background: '#f8fafc', borderRadius: 8, padding: '8px 10px', minWidth: 0 },
  label: { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' },
  value: { fontSize: 13.5, color: '#0f172a', marginTop: 2, wordBreak: 'break-word' },
  muted: { fontSize: 12, color: '#64748b' },
  section: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, marginBottom: 14 },
  bubble: { borderRadius: 10, padding: '8px 12px', marginBottom: 8, fontSize: 13.5, maxWidth: '100%', boxSizing: 'border-box' },
  userBubble: { background: '#f1f5f9' },
  adminBubble: { background: '#ecfdf5', border: '1px solid #bbf7d0' },
  noteBubble: { background: '#fffbeb', border: '1px dashed #f59e0b' },
  bubbleMeta: { display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', fontSize: 11.5, color: '#64748b', marginBottom: 3 },
  bubbleRole: { fontWeight: 700 },
  bubbleText: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#0f172a' },
  attach: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  attachLink: { fontSize: 12, color: '#0B6A4A', fontWeight: 600, wordBreak: 'break-all' },
  textarea: { width: '100%', boxSizing: 'border-box', borderRadius: 8, border: '1px solid #d1d5db', padding: 10, fontSize: 14, fontFamily: 'inherit', marginTop: 6 },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: 8 },
  primary: { padding: '9px 16px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: '#fff', fontWeight: 700, fontSize: 13.5, cursor: 'pointer' },
  secondary: { padding: '9px 16px', borderRadius: 8, border: '1px solid #f59e0b', background: '#fff', color: '#92400e', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' },
};
